import { AgentComponentMode, AgentComponentType } from '@reach/shared-types/agent-components';
import { Link } from 'react-router-dom';
import { appRoutes } from '../../../app-routes';
import { PageHeader } from '../../../components/common/PageHeader';
import { usePartnerProfile } from '../../../hooks/usePartnerSettings';
import { useAgentTemplates } from '../../ai-agents/agent-templates/useAgentTemplates';
import { STATUS_COPY } from '../agents-copy';
import { StatusNote } from '../StatusNote';
import { findVoiceTemplateId, useTemplateComponents } from '../useTemplateComponents';
import styles from './TenantPreviewPage.module.css';
import { TenantPreviewPane } from './TenantPreviewPane';
import { TenantPreviewSection } from './TenantPreviewSection';

export const TenantPreviewPage = () => {
  const { data: templates, isLoading: templatesAreLoading } = useAgentTemplates();
  const voiceTemplateId = findVoiceTemplateId(templates);
  const { data: templateComponents, isLoading: componentsAreLoading } =
    useTemplateComponents(voiceTemplateId);
  const { partner } = usePartnerProfile();
  const partnerName = partner?.name;

  const templateIsMissing = !templatesAreLoading && voiceTemplateId === undefined;
  const componentsFailedToLoad =
    Boolean(voiceTemplateId) && !componentsAreLoading && !templateComponents;
  const pageIsLoading = templatesAreLoading || componentsAreLoading;
  const visibleComponents = (templateComponents ?? []).filter(
    component => component.mode !== AgentComponentMode.HIDDEN,
  );
  const tenantsSeeNothing = Boolean(templateComponents) && visibleComponents.length === 0;

  const visibleOfType = (type: AgentComponentType) =>
    visibleComponents.filter(component => component.type === type);
  const guidance = visibleOfType(AgentComponentType.GUIDANCE);
  const skills = visibleOfType(AgentComponentType.SKILL);
  const escalations = visibleOfType(AgentComponentType.ESCALATION);
  const capabilitiesAreShown = guidance.length > 0 || skills.length > 0;
  const transferRulesAreShown = escalations.length > 0;

  return (
    <section className={styles.tenantPreviewPage}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <ol>
          <li>
            <Link to={appRoutes.AGENT_VOICE_TEMPLATE}>Voice template</Link>
          </li>
          <li aria-current="page">Preview as a tenant</li>
        </ol>
      </nav>
      <PageHeader
        title="Preview as a tenant"
        description="What tenants see in onboarding and settings, including changes you haven't synced yet."
      />
      {pageIsLoading && <StatusNote>{STATUS_COPY.loading}</StatusNote>}
      {templateIsMissing && (
        <StatusNote tone="error">{STATUS_COPY.loadError('Your Voice template')}</StatusNote>
      )}
      {componentsFailedToLoad && (
        <StatusNote tone="error">{STATUS_COPY.loadError('The components on this template')}</StatusNote>
      )}
      {tenantsSeeNothing && (
        <p className={styles.empty}>Tenants won't see anything from this template yet.</p>
      )}
      {capabilitiesAreShown && (
        <TenantPreviewPane
          title="Capabilities"
          description="How your AI phone behaves on every call, and what it can do for callers.">
          <TenantPreviewSection
            title="Guidance"
            description="Rules your AI phone follows in every conversation"
            components={guidance}
            partnerName={partnerName}
          />
          <TenantPreviewSection
            title="Skills"
            description="What your AI phone can do on a call, like taking in new clients or booking appointments"
            components={skills}
            partnerName={partnerName}
          />
        </TenantPreviewPane>
      )}
      {transferRulesAreShown && (
        <TenantPreviewPane
          title="Transfer rules"
          description="Rules for when your AI phone transfers a caller to a real person.">
          <TenantPreviewSection
            title="Default rules"
            description="Sends callers to your transfer number"
            components={escalations}
            partnerName={partnerName}
          />
        </TenantPreviewPane>
      )}
    </section>
  );
};
